import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import apiService from '../services/api';
import { useAuth } from '../auth/AuthContext';
import InvitationCelebration from '../components/InvitationCelebration';
import useInvitationCelebration from '../hooks/useInvitationCelebration';
import { STAGE_GRADIENT } from '../constants/rankColors';
import {
  SpecialOffer,
  SpecialOfferRespondStatus,
  StoreUser,
} from '../types';

// 諾否済みステータスか（draft/sent 以外は応答済み）。
const isResponded = (status: SpecialOffer['status']): boolean =>
  status === 'accepted' || status === 'declined' || status === 'expired';

// オファー種別のラベル。
const typeLabel = (t: SpecialOffer['offer_type']): string =>
  t === 'invitation' ? '招待状' : '特別オファー';

// 諾否ステータスのチップ（承諾/辞退/期限切れ）。背景は招待状の白カード上でも読めるよう調整可。
// 注: 'expired' は DB の CHECK 制約には存在するが、期限を設定する列もバッチも無いため
// 現状この状態には遷移しない（＝期限切れチップは表示されない）。将来の期限機能に備えて残置。
const StatusChip: React.FC<{ status: SpecialOffer['status']; onDark?: boolean }> = ({ status, onDark }) => {
  if (status === 'accepted') {
    return <Chip label="承諾済み" size="small" color="success" sx={{ height: 20, fontSize: '10px' }} />;
  }
  if (status === 'declined') {
    return <Chip label="辞退済み" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: onDark ? 'rgba(255,255,255,0.25)' : '#eee', color: onDark ? '#fff' : '#888' }} />;
  }
  if (status === 'expired') {
    return <Chip label="期限切れ" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: onDark ? 'rgba(255,255,255,0.25)' : '#eee', color: onDark ? '#fff' : '#888' }} />;
  }
  return null;
};

// 諾否ボタン（受ける / 見送る）。招待状ヒーロー上では白基調に切り替える。
const RespondActions: React.FC<{
  offer: SpecialOffer;
  busy: boolean;
  onRespond: (offerId: string, status: SpecialOfferRespondStatus) => void;
  onDark?: boolean;
}> = ({ offer, busy, onRespond, onDark }) => (
  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
    <Button
      fullWidth
      variant="contained"
      disabled={busy}
      startIcon={<CheckCircleRoundedIcon />}
      onClick={() => onRespond(offer.offer_id, 'accepted')}
      sx={
        onDark
          ? { borderRadius: 999, fontWeight: 700, bgcolor: '#fff', color: STAGE_GRADIENT.bottomTo, '&:hover': { bgcolor: '#f0f0f0' } }
          : { borderRadius: 999, fontWeight: 700, bgcolor: STAGE_GRADIENT.topFrom, '&:hover': { bgcolor: STAGE_GRADIENT.topTo } }
      }
    >
      受ける
    </Button>
    <Button
      fullWidth
      variant="outlined"
      disabled={busy}
      startIcon={<CloseRoundedIcon />}
      onClick={() => onRespond(offer.offer_id, 'declined')}
      sx={
        onDark
          ? { borderRadius: 999, fontWeight: 700, color: '#fff', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: '#fff' } }
          : { borderRadius: 999, fontWeight: 700, color: '#888', borderColor: '#ccc' }
      }
    >
      見送る
    </Button>
  </Box>
);

/**
 * 招待状ヒーローカード（設計書 §6.3 p.20 INVITATION）。
 * Official Career Talk / GIFT TICKET。「店長からの招待状」を紫グラデ背景（付録C.5）で描く。
 */
const InvitationCard: React.FC<{
  offer: SpecialOffer;
  busy: boolean;
  onRespond: (offerId: string, status: SpecialOfferRespondStatus) => void;
}> = ({ offer, busy, onRespond }) => {
  const responded = isResponded(offer.status);
  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 4,
        p: 2.5,
        mb: 2,
        color: '#fff',
        // 付録C.5: 上側 青紫 → 下側 マゼンタ紫（左下→右上の対角）
        background: 'linear-gradient(45deg, #7e318e 0%, #534dff 100%)',
      }}
    >
      {/* ラベル: Official Career Talk / GIFT TICKET */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <MailOutlineRoundedIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: '11px', letterSpacing: 2, fontWeight: 700, opacity: 0.9 }}>
          Official Career Talk
        </Typography>
        <Chip
          label="GIFT TICKET"
          size="small"
          sx={{
            height: 20,
            fontSize: '10px',
            fontWeight: 800,
            ml: 'auto',
            color: STAGE_GRADIENT.bottomTo,
            background: `linear-gradient(180deg, ${STAGE_GRADIENT.accentFrom} 0%, ${STAGE_GRADIENT.accentTo} 100%)`,
          }}
        />
      </Box>

      <Typography sx={{ fontSize: '12px', opacity: 0.85, mb: 0.5 }}>店長からの招待状</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '18px', mb: 1 }}>{offer.title}</Typography>

      {/* 白半透明オーバーレイのメッセージ枠（付録C.5） */}
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3, p: 1.5, mb: offer.reward_content ? 1 : 0 }}>
        <Typography sx={{ fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {offer.message_content}
        </Typography>
      </Box>

      {offer.reward_content && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 2, px: 1.5, py: 1 }}>
          <CardGiftcardRoundedIcon sx={{ color: STAGE_GRADIENT.accentTo, fontSize: 20 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '11px', color: '#9e9e9e', lineHeight: 1.2 }}>特典</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#B7553C' }}>{offer.reward_content}</Typography>
          </Box>
        </Box>
      )}

      {responded ? (
        <Box sx={{ mt: 1.5 }}>
          <StatusChip status={offer.status} onDark />
        </Box>
      ) : (
        <RespondActions offer={offer} busy={busy} onRespond={onRespond} onDark />
      )}
    </Paper>
  );
};

// 特別オファー（offer_type='special'）の標準白カード。
const SpecialOfferCard: React.FC<{
  offer: SpecialOffer;
  busy: boolean;
  onRespond: (offerId: string, status: SpecialOfferRespondStatus) => void;
}> = ({ offer, busy, onRespond }) => {
  const responded = isResponded(offer.status);
  return (
    <Paper elevation={1} sx={{ borderRadius: 3, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CardGiftcardRoundedIcon sx={{ color: STAGE_GRADIENT.accentTo, fontSize: 20 }} />
        <Chip
          label={typeLabel(offer.offer_type)}
          size="small"
          sx={{
            height: 20,
            fontSize: '10px',
            fontWeight: 700,
            bgcolor: `${STAGE_GRADIENT.accentTo}1A`,
            color: '#B7553C',
          }}
        />
        <StatusChip status={offer.status} />
      </Box>

      <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#1a1a1a', mb: 0.5 }}>
        {offer.title}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: '#555', whiteSpace: 'pre-wrap', mb: offer.reward_content ? 1 : 0 }}>
        {offer.message_content}
      </Typography>
      {offer.reward_content && (
        <Box sx={{ bgcolor: '#FFF8E6', borderRadius: 2, px: 1.5, py: 1, mb: 1 }}>
          <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>特典</Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#B7553C' }}>
            {offer.reward_content}
          </Typography>
        </Box>
      )}

      {!responded && <RespondActions offer={offer} busy={busy} onRespond={onRespond} />}
    </Paper>
  );
};

// 受信オファー / 招待状 1件のカード（種別で描画を分岐）。
const OfferCard: React.FC<{
  offer: SpecialOffer;
  busy: boolean;
  onRespond: (offerId: string, status: SpecialOfferRespondStatus) => void;
}> = ({ offer, busy, onRespond }) =>
  offer.offer_type === 'invitation' ? (
    <InvitationCard offer={offer} busy={busy} onRespond={onRespond} />
  ) : (
    <SpecialOfferCard offer={offer} busy={busy} onRespond={onRespond} />
  );

/**
 * 特別オファー画面（旧称スカウト。設計書 §5.3 / §6.3、文言変更は修正依頼 #49）。ルートは互換のため /scout のまま。
 * - INVITATION（p.20 / 紫グラデ ヒーロー）/ 特別オファー 受信箱（offer_type で区別）と諾否（受ける / 見送る）。
 * - GET SKILL お祝い演出（未読スキルを 1 回だけ表示。表示時に mark-seen で既読化。共有フックで /mypage と共通）。
 *
 * 認証はワーカー LINE フロント規約に合わせ line_access_token 自己所有経路（api.ts buildWorkerQuery）。
 * オファー/招待状は宛先の store_user 単位で送られるため、複数店舗登録ワーカーが取り漏らさないよう
 * userId（AuthContext）→ getStoreUser で取得した全店舗分を対象にする
 * （Layout の useUnreadSpecialOffers / useGetSkillCelebration と同じ全店舗集約の方針）。
 */
const ScoutPage: React.FC = () => {
  const { userId } = useAuth();

  const [storeUserIds, setStoreUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [offers, setOffers] = useState<SpecialOffer[]>([]);
  const [respondingId, setRespondingId] = useState<string | null>(null);

  // 招待状演出（特別オファーページ遷移時に未確認招待状があれば1回だけ表示）。
  const { pendingInvitations, closeInvitation } = useInvitationCelebration(storeUserIds);

  // 対象 store_user を解決（全登録店舗）。
  const resolveStoreUsers = useCallback(async (): Promise<string[]> => {
    if (!userId) return [];
    const res = await apiService.getStoreUser(userId);
    if (res.success && res.data) {
      return (res.data as StoreUser[]).map((su) => su.id);
    }
    return [];
  }, [userId]);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const suIds = await resolveStoreUsers();
      setStoreUserIds(suIds);
      if (suIds.length === 0) return;

      const offersRes = await Promise.all(suIds.map((suId) => apiService.getSpecialOffers(suId)));
      const allOffers = offersRes
        .filter((res) => res.success && res.data)
        .flatMap((res) => res.data as SpecialOffer[]);
      // 受信箱は日付の新しい順（複数店舗をマージするため API の並びに依存せずフロントで保証する）。
      allOffers.sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
      );
      setOffers(allOffers);
    } catch {
      // 無認証 / ネットワーク等は空表示に倒す。
    } finally {
      setLoading(false);
    }
  }, [resolveStoreUsers]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleRespond = async (offerId: string, status: SpecialOfferRespondStatus) => {
    const offer = offers.find((o) => o.offer_id === offerId);
    if (!offer) return;
    setRespondingId(offerId);
    try {
      const res = await apiService.respondSpecialOffer(offer.store_user_id, offerId, status);
      if (res.success && res.data) {
        setOffers((prev) => prev.map((o) => (o.offer_id === offerId ? res.data! : o)));
      }
    } finally {
      setRespondingId(null);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* 招待状演出（特別オファーページ初回遷移時に未確認招待状があれば1回だけ） */}
      <InvitationCelebration
        invitations={pendingInvitations}
        onLater={closeInvitation}
        onConfirm={closeInvitation}
      />

      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 2, pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#3c3c3c' }}>特別オファー</Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 4 }}>
          {offers.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '50vh',
                gap: 1.5,
                color: '#898989',
              }}
            >
              <MailOutlineRoundedIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                受信したオファー・招待状はありません。
              </Typography>
            </Box>
          ) : (
            offers.map((offer) => (
              <OfferCard
                key={offer.offer_id}
                offer={offer}
                busy={respondingId === offer.offer_id}
                onRespond={handleRespond}
              />
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default ScoutPage;
